import { useState, useEffect, useCallback } from 'react';
import api, { formatoFecha } from '@/lib/api';
import { getWhatsAppLink } from '@/lib/whatsapp';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, Plus, Clock, Check, X, MessageCircle, Phone } from 'lucide-react';
import { toast } from 'sonner';

const ESTADOS_CITA = {
  pendiente: { label: 'Pendiente', clase: 'bg-amber-500/20 text-amber-400 border-amber-800' },
  confirmada: { label: 'Confirmada', clase: 'bg-blue-500/20 text-blue-400 border-blue-800' },
  completada: { label: 'Completada', clase: 'bg-green-500/20 text-green-400 border-green-800' },
  cancelada: { label: 'Cancelada', clase: 'bg-red-500/20 text-red-400 border-red-800' },
};

function formatearTelWhatsApp(telefono) {
  if (!telefono) return '';
  let tel = telefono.replace(/[\s\-()+']/g, '');
  if (tel.startsWith('52')) return tel;
  if (tel.startsWith('1') && tel.length === 11) return tel;
  if (tel.length === 10) return '52' + tel;
  return tel;
}

function generarLinkWhatsApp(telefono, cita) {
  const tel = formatearTelWhatsApp(telefono);
  if (!tel) return '#';
  const msg = `Hola! Le recordamos su cita en nuestro taller:\n\nFecha: ${cita.fecha}\nHora: ${cita.hora}\nServicio: ${cita.tipo_servicio}\nVehículo: ${cita.vehiculo_info || 'N/A'}\n\nLe esperamos! Gracias.`;
  return getWhatsAppLink(tel, msg);
}

export default function Citas() {
  const [citas, setCitas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [form, setForm] = useState({
    cliente_id: '', vehiculo_id: '', fecha: '', hora: '', tipo_servicio: '', notas: ''
  });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/citas', { params: { estado: filtroEstado === 'todos' ? '' : filtroEstado } });
      setCitas(res.data);
    } catch { toast.error('Error al cargar citas'); }
    finally { setCargando(false); }
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirDialog = async () => {
    try {
      const [cRes, vRes] = await Promise.all([
        api.get('/clientes?busqueda='),
        api.get('/vehiculos?busqueda=&cliente_id=')
      ]);
      setClientes(cRes.data);
      setVehiculos(vRes.data);
    } catch { /* ignore */ }
    setForm({ cliente_id: '', vehiculo_id: '', fecha: '', hora: '', tipo_servicio: '', notas: '' });
    setDialog(true);
  };

  const crear = async () => {
    if (!form.cliente_id || !form.fecha || !form.tipo_servicio) {
      toast.error('Cliente, fecha y tipo de servicio son obligatorios');
      return;
    }
    setGuardando(true);
    try {
      await api.post('/citas', form);
      toast.success('Cita agendada exitosamente');
      setDialog(false);
      cargar();
    } catch { toast.error('Error al crear cita'); }
    finally { setGuardando(false); }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      await api.patch(`/citas/${id}/estado`, { estado });
      toast.success(`Cita ${estado}`);
      cargar();
    } catch { toast.error('Error al actualizar cita'); }
  };

  const vehiculosCliente = vehiculos.filter(v => v.cliente_id === form.cliente_id);

  // Group by date
  const citasPorFecha = {};
  citas.forEach(c => {
    const key = c.fecha || 'Sin fecha';
    if (!citasPorFecha[key]) citasPorFecha[key] = [];
    citasPorFecha[key].push(c);
  });

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="citas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-amber-500" /> Agenda de Citas
        </h1>
        <div className="flex items-center gap-3">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="filtro-estado-cita">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="todos" className="text-slate-200">Todos</SelectItem>
              <SelectItem value="pendiente" className="text-slate-200">Pendiente</SelectItem>
              <SelectItem value="confirmada" className="text-slate-200">Confirmada</SelectItem>
              <SelectItem value="completada" className="text-slate-200">Completada</SelectItem>
              <SelectItem value="cancelada" className="text-slate-200">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={abrirDialog} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nueva-cita-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Cita
          </Button>
        </div>
      </div>

      {citas.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="py-16 text-center">
            <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay citas programadas</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(citasPorFecha).map(([fecha, citasDia]) => (
          <div key={fecha}>
            <h2 className="text-sm font-medium uppercase tracking-wider text-slate-500 mb-3">
              {formatoFecha(fecha)}
            </h2>
            <div className="space-y-2">
              {citasDia.map(c => {
                const est = ESTADOS_CITA[c.estado] || ESTADOS_CITA.pendiente;
                return (
                  <Card key={c.id} className="bg-slate-900/40 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`cita-card-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Clock className="w-4 h-4 text-amber-500" />
                            <span className="text-sm font-bold text-slate-200">{c.hora || '--:--'}</span>
                            <Badge className={est.clase + ' text-xs'}>{est.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-200 font-medium">{c.cliente_nombre}</p>
                          <p className="text-xs text-slate-500">{c.vehiculo_info} &middot; {c.tipo_servicio}</p>
                          {c.notas && <p className="text-xs text-slate-600 mt-1">{c.notas}</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {c.cliente_telefono && (
                            <a href={generarLinkWhatsApp(c.cliente_telefono, c)}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-sm transition-colors"
                              data-testid={`whatsapp-cita-${c.id}`}>
                              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                            </a>
                          )}
                          {c.cliente_telefono && (
                            <a href={`tel:${c.cliente_telefono}`}
                              className="inline-flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-sm transition-colors"
                              data-testid={`llamar-cita-${c.id}`}>
                              <Phone className="w-3.5 h-3.5" /> Llamar
                            </a>
                          )}
                          {c.estado === 'pendiente' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'confirmada')}
                                className="text-blue-400 hover:text-blue-300 text-xs" data-testid={`confirmar-cita-${c.id}`}>
                                <Check className="w-3.5 h-3.5 mr-1" /> Confirmar
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'cancelada')}
                                className="text-red-400 hover:text-red-300 text-xs" data-testid={`cancelar-cita-${c.id}`}>
                                <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                              </Button>
                            </>
                          )}
                          {c.estado === 'confirmada' && (
                            <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'completada')}
                              className="text-green-400 hover:text-green-300 text-xs" data-testid={`completar-cita-${c.id}`}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Completar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="nueva-cita-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Nueva Cita</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Cliente *</Label>
              <Select value={form.cliente_id} onValueChange={v => setForm({...form, cliente_id: v, vehiculo_id: ''})}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-cliente-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-slate-200">{c.nombre_completo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.cliente_id && vehiculosCliente.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Vehículo</Label>
                <Select value={form.vehiculo_id} onValueChange={v => setForm({...form, vehiculo_id: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-vehiculo-select">
                    <SelectValue placeholder="Seleccionar vehiculo" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {vehiculosCliente.map(v => (
                      <SelectItem key={v.id} value={v.id} className="text-slate-200">{v.marca} {v.modelo} - {v.placas}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Fecha *</Label>
                <Input type="date" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-fecha-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Hora</Label>
                <Input type="time" value={form.hora} onChange={e => setForm({...form, hora: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-hora-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Tipo de Servicio *</Label>
              <Input value={form.tipo_servicio} onChange={e => setForm({...form, tipo_servicio: e.target.value})}
                placeholder="Ej: Cambio de aceite, Revision general..."
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-tipo-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-notas-input" />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crear} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-cita-btn">
              {guardando ? 'Guardando...' : 'Agendar Cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
